/**
 * Coach Virtual (MYIHTP) — Roda da Vida + Auto-análise PNL
 * ---------------------------------------------------------------
 * Page composta por 3 sub-vistas controladas por `view`:
 *   - "form"     → formulário de nova avaliação (ou edição)
 *   - "resultado" → gráfico radar + insights + auto-análise
 *   - "historico" → lista + comparação de avaliações passadas
 *
 * Design: paleta ihtp (navy #0F1829, verde #4A5E2E, taupe #978D78) com
 * cards em fundo #FBFAF7 e headings sem sombra. Sem emojis nos títulos —
 * usamos ícones lucide-react (Compass, Sparkles, Brain).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Compass, Sparkles, Brain, ChevronLeft, RefreshCw, History,
  Loader2, Target, HeartHandshake, AlertTriangle, Wand2, BookOpen,
  MessageSquareQuote,
} from "lucide-react";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Tooltip, Legend,
} from "recharts";
import { myApi } from "../api";
import RodaForm from "./coach/RodaForm";
import RodaInsights from "./coach/RodaInsights";
import RodaAnalise from "./coach/RodaAnalise";
import RodaHistorico from "./coach/RodaHistorico";
import MentorChat from "./coach/MentorChat";
import RodaCasal from "./coach/RodaCasal";
import SelfTalk from "./coach/SelfTalk";

const NAVY = "#0F1829";
const GREEN = "#4A5E2E";
const TAUPE = "#978D78";

const isMobile = () => typeof window !== "undefined" && window.innerWidth < 640;

export default function CoachVirtual() {
  const [view, setView] = useState("form"); // form | resultado | historico
  const [meta, setMeta] = useState({ areas: [] });
  const [current, setCurrent] = useState(null); // avaliação em foco
  const [savingId, setSavingId] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    myApi.coachRodaMeta()
      .then(setMeta)
      .catch((e) => setErr(e.message || "Falha ao carregar áreas."));
  }, []);

  const onSaved = (doc) => {
    setCurrent(doc);
    setSavingId(doc.id);
    setView("resultado");
  };

  const onPickFromHistory = async (id) => {
    try {
      const doc = await myApi.coachRodaGet(id);
      setCurrent(doc);
      setView("resultado");
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="coach-virtual-page">
      <Header view={view} onBack={() => setView("form")} onHistory={() => setView("historico")} onMentor={() => setView("mentor")} onCasal={() => setView("casal")} onSelfTalk={() => setView("selftalk")} />

      <CoachIhtpBanner />

      {err && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm px-4 py-3" data-testid="coach-error">
          {err}
        </div>
      )}

      {view === "form" && (
        <RodaForm
          areas={meta.areas}
          initial={current}
          onSaved={onSaved}
          onError={setErr}
        />
      )}

      {view === "resultado" && current && (
        <Resultado
          assessment={current}
          onEdit={() => setView("form")}
          onError={setErr}
          onRefresh={(doc) => setCurrent(doc)}
        />
      )}

      {view === "historico" && (
        <RodaHistorico onPick={onPickFromHistory} onError={setErr} />
      )}
      {view === "mentor" && <MentorChat />}
      {view === "casal" && <RodaCasal onError={setErr} />}
      {view === "selftalk" && <SelfTalk onError={setErr} />}
    </div>
  );
}

function CoachIhtpBanner() {
  return (
    <div
      className="mb-6 rounded-2xl bg-[#0F1829] text-white px-4 sm:px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 shadow-sm"
      data-testid="coach-ihtp-banner"
    >
      <div className="flex items-center gap-3 flex-1">
        <div className="w-10 h-10 rounded-full grid place-items-center bg-[#4A5E2E] shrink-0">
          <HeartHandshake size={20} />
        </div>
        <p className="text-sm sm:text-base font-medium leading-snug">
          Precisas do apoio de um Coach IHTP para te ajudar?
        </p>
      </div>
      <a
        href="https://ihavethepower.net/coaching/"
        target="_blank"
        rel="noopener noreferrer"
        className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-5 py-2.5 sm:py-2 rounded-full bg-[#4A5E2E] hover:bg-[#3d4e26] text-white text-sm font-semibold transition-colors shrink-0"
        data-testid="coach-ihtp-banner-cta"
      >
        Reserva já
      </a>
    </div>
  );
}

function Header({ view, onBack, onHistory, onMentor, onCasal, onSelfTalk }) {
  return (
    <div className="mb-6">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-full grid place-items-center bg-[#0F1829] text-white">
          <Compass size={22} />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif tracking-tight text-[#0F1829]" data-testid="coach-title">
            Coach Virtual
          </h1>
          <p className="text-sm text-[#6B7280]">
            Roda da Vida + Auto-análise PNL — Bandler
          </p>
        </div>
        <div className="w-full sm:w-auto sm:ml-auto flex gap-2">
          {view !== "form" && (
            <button
              onClick={onBack}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1 px-3 py-2 sm:py-1.5 rounded-md text-sm border border-[#E5E7EB] text-[#0F1829] hover:bg-[#F3F4F6]"
              data-testid="coach-btn-new"
            >
              <ChevronLeft size={16} /> Nova avaliação
            </button>
          )}
          {view !== "casal" && (
            <button
              onClick={onCasal}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1 px-3 py-2 sm:py-1.5 rounded-md text-sm border border-[#4A5E2E] text-[#4A5E2E] hover:bg-[#F3F4F6]"
              data-testid="coach-btn-casal"
            >
              <HeartHandshake size={16} /> Casal
            </button>
          )}
          {view !== "selftalk" && (
            <button
              onClick={onSelfTalk}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1 px-3 py-2 sm:py-1.5 rounded-md text-sm border border-[#4A5E2E] text-[#4A5E2E] hover:bg-[#F3F4F6]"
              data-testid="coach-btn-selftalk"
            >
              <MessageSquareQuote size={16} /> Self-Talk
            </button>
          )}
          {view !== "mentor" && (
            <button
              onClick={onMentor}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1 px-3 py-2 sm:py-1.5 rounded-md text-sm bg-[#4A5E2E] text-white hover:opacity-90"
              data-testid="coach-btn-mentor"
            >
              <BookOpen size={16} /> Mentor IHTP
            </button>
          )}
          {view !== "historico" && (
            <button
              onClick={onHistory}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1 px-3 py-2 sm:py-1.5 rounded-md text-sm bg-[#0F1829] text-white hover:opacity-90"
              data-testid="coach-btn-history"
            >
              <History size={16} /> Histórico
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Resultado({ assessment, onEdit, onError, onRefresh }) {
  const [insights, setInsights] = useState(assessment.insights || null);
  const [analise, setAnalise] = useState(assessment.analise || null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [loadingAnalise, setLoadingAnalise] = useState(false);

  useEffect(() => {
    setInsights(assessment.insights || null);
    setAnalise(assessment.analise || null);
  }, [assessment.id, assessment.insights, assessment.analise]);

  const radarData = useMemo(() => {
    const scores = assessment.scores || {};
    return Object.entries(scores).map(([area, v]) => ({
      area,
      // encurtamos "Desenvolvimento Pessoal" para caber no gráfico
      label: area === "Desenvolvimento Pessoal" ? "Desenv. Pessoal" : area,
      valor: Number(v) || 0,
    }));
  }, [assessment.scores]);

  const gerarInsights = async () => {
    setLoadingInsights(true);
    try {
      const r = await myApi.coachRodaInsights(assessment.id);
      setInsights(r.insights);
      onRefresh({ ...assessment, insights: r.insights });
    } catch (e) {
      onError(e.message);
    } finally {
      setLoadingInsights(false);
    }
  };

  const gerarAnalise = async () => {
    setLoadingAnalise(true);
    try {
      const r = await myApi.coachRodaAnalise(assessment.id);
      setAnalise(r.analise);
      onRefresh({ ...assessment, analise: r.analise });
    } catch (e) {
      onError(e.message);
    } finally {
      setLoadingAnalise(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Gráfico Radar */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-6 shadow-sm" data-testid="coach-radar-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-[#0F1829]">A tua Roda da Vida</h2>
          <button
            onClick={onEdit}
            className="text-xs text-[#4A5E2E] hover:underline"
            data-testid="coach-btn-edit"
          >
            Editar respostas
          </button>
        </div>
        <div className="w-full h-[300px] sm:h-[420px]" data-testid="coach-radar">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} outerRadius={isMobile() ? "65%" : "80%"}>
              <PolarGrid stroke="#E5E7EB" />
              <PolarAngleAxis dataKey="label" tick={{ fill: "#0F1829", fontSize: isMobile() ? 10 : 12 }} />
              <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: "#978D78", fontSize: 10 }} />
              <Radar
                name="Pontuação (0-10)"
                dataKey="valor"
                stroke="#4A5E2E"
                fill="#4A5E2E"
                fillOpacity={0.35}
              />
              <Tooltip
                formatter={(v) => [`${v}/10`, "Pontuação"]}
                contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }}
              />
              <Legend />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Insights */}
      <RodaInsights
        insights={insights}
        loading={loadingInsights}
        onGenerate={gerarInsights}
      />

      {/* Auto-análise PNL global + por área */}
      <RodaAnalise
        assessment={assessment}
        analise={analise}
        loading={loadingAnalise}
        onGenerate={gerarAnalise}
        onRefresh={onRefresh}
        onError={onError}
      />
    </div>
  );
}
